import { useEffect, useMemo, useState } from "react";
import {
  ChevronDown,
  ChevronRight,
  Inbox,
  Loader2,
  MessageSquareText,
} from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Textarea } from "@/components/ui/textarea";
import {
  usePatientDoctorMessageThreads,
  useDoctorThreadByLabVisit,
  useReplyDoctorMessageThread,
  useSetDoctorThreadStatus,
  type DoctorMessageThreadRow,
} from "@/hooks/api";
import { cn } from "@/lib/cn";
import { ReadState } from "@/components/doctor-messages/read-state";
import { toast } from "sonner";

function formatDateTime(dateStr: string): string {
  return new Date(dateStr).toLocaleString("nb-NO", {
    day: "2-digit",
    month: "short",
    year: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/**
 * Doctor-message threads for one customer on the user-detail page.
 * Shows ALL of the user's threads across lab visits — the thread for the
 * currently selected visit is pinned first and expanded, so the doctor finds
 * the conversation for the lab visit in question without hunting.
 */
export function DoctorMessagesPanel({
  authUserId,
  selectedLabVisitId,
  autoScroll,
}: {
  authUserId: string;
  selectedLabVisitId: string | null;
  /** Deep link from the kundebehandling Messages tab: scroll here once loaded. */
  autoScroll?: boolean;
}) {
  const { data: threads, isLoading } = usePatientDoctorMessageThreads(authUserId);

  useEffect(() => {
    if (!autoScroll || isLoading || !threads || threads.length === 0) return;
    document
      .getElementById("meldinger")
      ?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [autoScroll, isLoading, threads]);

  const ordered = useMemo(() => {
    const list = [...(threads ?? [])];
    // Selected visit's thread first, then by last activity (server order).
    if (selectedLabVisitId) {
      list.sort(
        (a, b) =>
          (b.labVisitId === selectedLabVisitId ? 1 : 0) -
          (a.labVisitId === selectedLabVisitId ? 1 : 0),
      );
    }
    return list;
  }, [threads, selectedLabVisitId]);

  if (isLoading) return null;
  // Nothing to answer until the customer has started a conversation.
  if (!threads || threads.length === 0) return null;

  return (
    <Card id="meldinger">
      <CardContent className="p-4 space-y-3">
        <div className="flex items-center gap-2">
          <Inbox className="h-4 w-4 text-admin-primary" />
          <h3 className="text-sm font-semibold">Pasientdialog</h3>
          <span className="text-xs text-admin-text-subtle">
            {threads.length} dialog{threads.length === 1 ? "" : "er"}
          </span>
        </div>
        <div className="space-y-2">
          {ordered.map((thread) => (
            <ThreadItem
              key={thread.id}
              thread={thread}
              isCurrentVisit={thread.labVisitId === selectedLabVisitId}
              defaultOpen={
                thread.labVisitId === selectedLabVisitId &&
                thread.status === "open"
              }
            />
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function ThreadItem({
  thread,
  isCurrentVisit,
  defaultOpen,
}: {
  thread: DoctorMessageThreadRow;
  isCurrentVisit: boolean;
  defaultOpen: boolean;
}) {
  const [open, setOpen] = useState(defaultOpen);
  const { data: detail, isLoading: detailLoading } =
    useDoctorThreadByLabVisit(open ? thread.labVisitId : null);

  return (
    <div
      className={cn(
        "rounded-md border",
        isCurrentVisit
          ? "border-admin-primary/60"
          : "border-admin-card-border",
        thread.status === "open" && "bg-admin-card/50",
      )}
    >
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center gap-2 px-3 py-2 text-left cursor-pointer"
      >
        {open ? (
          <ChevronDown className="h-4 w-4 shrink-0 text-admin-text-subtle" />
        ) : (
          <ChevronRight className="h-4 w-4 shrink-0 text-admin-text-subtle" />
        )}
        <span className="text-xs text-admin-text-secondary whitespace-nowrap">
          Besøk {thread.labVisitDate
            ? new Date(thread.labVisitDate).toLocaleDateString("nb-NO", {
                day: "2-digit",
                month: "short",
                year: "numeric",
              })
            : "–"}
        </span>
        {thread.status === "open" ? (
          <Badge className="bg-admin-success-400/20 text-admin-success-400 border-0 text-[10px]">
            Åpen
          </Badge>
        ) : (
          <Badge variant="secondary" className="text-[10px]">
            Lukket
          </Badge>
        )}
        <ReadState
          hasUnread={thread.hasUnread}
          userReadAt={thread.userReadAt}
          className="text-[11px]"
        />
        <span className="ml-auto text-[11px] text-admin-text-subtle whitespace-nowrap">
          {thread.messageCount} meldinger
        </span>
      </button>

      {open && (
        <div className="px-3 pb-3 space-y-3">
          {detailLoading || !detail ? (
            <div className="flex justify-center py-3">
              <Loader2 className="h-4 w-4 animate-spin text-admin-text-subtle" />
            </div>
          ) : (
            <>
              <div className="space-y-2 max-h-80 overflow-y-auto">
                {detail.messages.map((m) => (
                  <div
                    key={m.id}
                    className={cn(
                      "rounded-md px-3 py-2 text-sm whitespace-pre-wrap",
                      m.senderKind === "doctor"
                        ? "bg-admin-primary/10 ml-6"
                        : "bg-admin-card mr-6",
                    )}
                  >
                    <div className="flex items-center gap-2 text-[10px] text-admin-text-subtle mb-0.5">
                      <MessageSquareText className="h-3 w-3" />
                      {m.senderKind === "doctor"
                        ? m.senderName ?? "Lege"
                        : "Kunde"}
                      <span>·</span>
                      {formatDateTime(m.createdAt)}
                    </div>
                    {m.text}
                  </div>
                ))}
              </div>
              {detail.thread.status === "open" ? (
                <ReplyComposer threadId={thread.id} />
              ) : (
                <div className="flex items-center gap-2">
                  <p className="text-xs text-admin-text-subtle">
                    {thread.closedAt
                      ? `Dialogen er lukket ${formatDateTime(thread.closedAt)}.`
                      : "Dialogen er lukket."}
                  </p>
                  <ThreadStatusButton threadId={thread.id} status="closed" />
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}

function ReplyComposer({ threadId }: { threadId: string }) {
  const [text, setText] = useState("");
  const reply = useReplyDoctorMessageThread();

  const send = (close: boolean) => {
    const body = text.trim();
    if (!body || reply.isPending) return;
    reply.mutate(
      { threadId, text: body, close },
      {
        onSuccess: () => {
          setText("");
          toast.success(close ? "Svar sendt og dialogen lukket" : "Svar sendt");
        },
        onError: () => toast.error("Kunne ikke sende svaret"),
      },
    );
  };

  return (
    <div className="space-y-2">
      <Textarea
        value={text}
        onChange={(e) => setText(e.target.value)}
        placeholder="Svar til kunden…"
        rows={3}
      />
      <div className="flex gap-2">
        <Button
          size="sm"
          disabled={!text.trim() || reply.isPending}
          onClick={() => send(false)}
        >
          Svar
        </Button>
        <Button
          size="sm"
          variant="outline"
          disabled={!text.trim() || reply.isPending}
          onClick={() => send(true)}
        >
          Svar og lukk
        </Button>
        <ThreadStatusButton
          threadId={threadId}
          status="open"
          className="ml-auto"
        />
      </div>
    </div>
  );
}

/** "Lukk uten svar" on an open thread, "Gjenåpne" on a closed one. Neither
 *  sends the customer a message or a push. */
function ThreadStatusButton({
  threadId,
  status,
  className,
}: {
  threadId: string;
  status: "open" | "closed";
  className?: string;
}) {
  const setStatus = useSetDoctorThreadStatus();
  const next = status === "open" ? "closed" : "open";

  return (
    <Button
      size="sm"
      variant="ghost"
      className={className}
      disabled={setStatus.isPending}
      onClick={() =>
        setStatus.mutate(
          { threadId, status: next },
          {
            onSuccess: () =>
              toast.success(
                next === "closed" ? "Dialogen er lukket" : "Dialogen er gjenåpnet",
              ),
            onError: () => toast.error("Kunne ikke endre dialogen"),
          },
        )
      }
    >
      {next === "closed" ? "Lukk uten svar" : "Gjenåpne"}
    </Button>
  );
}
